import cadviewer from "cadviewer";
import classNames from "classnames";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useContext, useState } from "react";
import { useAlert } from "react-alert";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  RightAction,
  SideBarLeftPanel,
  Tooltip,
  VersionDisplay,
} from "../../components";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";

interface EyeIconPasswordProps {
  show: boolean;
  onClick: () => void;
}

const EyeIconPassword = (props: EyeIconPasswordProps) => {
  const { show, onClick } = props;
  return (
    <div
      onClick={onClick}
      className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
    >
      {!show ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-6 h-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
          />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-6 h-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
          />
        </svg>
      )}
    </div>
  );
};

export const Profile = () => {
  const { config } = useConfig();
  const [leftPanelOpen, setLeftPanelOpen] = useState(
    config.defaultLeftPanelOpen
  );
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<File | null>(null);
  const [showPasswordOld, setShowPasswordOld] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const navigate = useNavigate();

  const { user, setUser } = useContext(CadviewerContext);

  const alert = useAlert();

  const handleAvatarChange = (event: any) => {
    const file = event.target.files[0];

    // test if file is an image and less than 1MB
    if (file && !["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      return;
    }

    if (file && file.size > 1024 * 1024) {
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };

    if (file) {
      reader.readAsDataURL(file);
      setAvatar(file);
    } else {
      setAvatarPreview(null); // Reset preview if no file selected
    }
  };

  console.log({ user });

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    username: Yup.string()
      .min(5, "Username must be at least 5 characters")
      .required("Username is required"),
    avatar: Yup.mixed()
      .nullable()
      .test(
        "fileSize",
        "File too large (max 1MB)",
        (value: any) => !value || (value && value.size <= 1024 * 1024)
      )
      .test(
        "fileType",
        "Unsupported file format (accepted: JPG, JPEG, PNG)",
        (value: any) =>
          !value ||
          (value &&
            ["image/jpeg", "image/png", "image/jpg"].includes(value.type))
      ),
  });

  const validationSchemaPassword = Yup.object().shape({
    current_password: Yup.string()
      // .min(6, 'Password must be at least 6 characters')
      .required("Current password is required"),
    new_password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("New password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("new_password"), ""], "Passwords must match")
      .required("Password confirmation is required"),
  });

  return (
    <div
      className={classNames({
        "h-screen max-h-screen overflow-y-hidden": true,
        "min-h-screen h-full w-full": true,
        "transition-[grid-template-columns] duration-300 ease-in-out": true,
      })}
    >
      <PanelGroup
        autoSaveId="main-content-panel"
        direction="horizontal"
        onLayout={(sizes) => cadviewer.cvjs_hideOnlyPop()}
      >
        {leftPanelOpen && (
          <>
            <Panel
              /*onResize={(size) => cadviewer.cvjs_hideOnlyPop()}*/ order={1}
              defaultSize={config.leftPaneDefaultWidth}
            >
              {/* Left Pane: put icon for open it like panel */}
              <SideBarLeftPanel
                leftPanelOpen={leftPanelOpen}
                setLeftPanelOpen={setLeftPanelOpen}
              />
            </Panel>
            {/* @ts-ignore */}

            <PanelResizeHandle className="w-2 bg-gray-300 relative">
              <div
                className="h-8 w-4 bg-gray-300 flex items-center justify-center"
                style={{
                  zIndex: 1000,
                  position: "absolute",
                  bottom: 0,
                  top: "calc(50% - 16px)",
                }}
              >
                <svg
                  width="20px"
                  height="32px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="Interface / Drag_Horizontal">
                    <g id="Vector">
                      <path
                        d="M14 18C14 18.5523 14.4477 19 15 19C15.5523 19 16 18.5523 16 18C16 17.4477 15.5523 17 15 17C14.4477 17 14 17.4477 14 18Z"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 18C8 18.5523 8.44772 19 9 19C9.55228 19 10 18.5523 10 18C10 17.4477 9.55228 17 9 17C8.44772 17 8 17.4477 8 18Z"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 12C14 12.5523 14.4477 13 15 13C15.5523 13 16 12.5523 16 12C16 11.4477 15.5523 11 15 11C14.4477 11 14 11.4477 14 12Z"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 12C8 12.5523 8.44772 13 9 13C9.55228 13 10 12.5523 10 12C10 11.4477 9.55228 11 9 11C8.44772 11 8 11.4477 8 12Z"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 6C14 6.55228 14.4477 7 15 7C15.5523 7 16 6.55228 16 6C16 5.44772 15.5523 5 15 5C14.4477 5 14 5.44772 14 6Z"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 6C8 6.55228 8.44772 7 9 7C9.55228 7 10 6.55228 10 6C10 5.44772 9.55228 5 9 5C8.44772 5 8 5.44772 8 6Z"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  </g>
                </svg>
              </div>
            </PanelResizeHandle>
          </>
        )}
        <Panel order={2} /*onResize={(size) => cadviewer.cvjs_hideOnlyPop()}*/>
          {/* Main conntent */}
          <div className="h-screen max-h-screen overflow-y-hidden flex flex-col">
            <div className="w-full bg-gray-100 min-h-20 flex justify-between items-center px-14 py-2">
              <Tooltip text={config.hambugerMenuTooltip}>
                <button
                  className="-ml-14 mr-5 bg-sidebar-hamburger-background  text-sidebar-hamburger-icon p-3"
                  onClick={() => setLeftPanelOpen((state: any) => !state)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                    />
                  </svg>
                </button>
              </Tooltip>
              <Link to={"/"} className={"flex"}>
                {config.topBarLogo && (
                  <img
                    src={config.topBarLogo}
                    alt="CADViewer Logo"
                    className="h-11"
                  />
                )}
                {!leftPanelOpen && (
                  <img
                    src={"https://cadviewer.com/images/cadviewer/cv-logo.gif"}
                    alt="CADViewer Logo"
                    className="ml-2 h-11"
                  />
                )}
              </Link>
              <div className="flex-1 flex flex-col justify-start items-start ml-4">
                <div>
                  <h4>
                    <b>My Profile</b>
                  </h4>
                </div>
              </div>
              <RightAction />
            </div>
            {!user ? (
              <div>Loading...</div>
            ) : (
              <main
                className={classNames({
                  "h-full relative flex-1  overflow-x-hidden": true,
                })}
              >
                {/* Settings forms */}
                <div className="divide-y divide-black/5">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
                    <div className={"flex justify-between"}>
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-black">
                          Personal Information
                        </h2>
                        <p className="mt-1 text-sm leading-6 text-gray-400">
                          Use a permanent address where you can receive mail.
                        </p>
                      </div>
                      <div className={"justify-end items-start flex md:hidden"}>
                        <button
                          onClick={() => {
                            navigate("/");
                          }}
                          className={`inline-flex items-center gap-x-1.5 rounded-md bg-primary-600 px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 mr-2
							        ${
                        config.helperMethodsActionSize === "small"
                          ? " text-xs my-1 mr-1 py-[2px] px-1"
                          : config.helperMethodsActionSize === "large"
                          ? " text-base my-2 mr-2 py-2 px-3"
                          : " text-sm my-2 mr-2 py-1 px-3"
                      }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-6"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                            />
                          </svg>
                          <span>{config.settingsGoHomepageButtonText}</span>
                        </button>
                      </div>
                    </div>
                    <Formik
                      initialValues={{
                        first_name: user?.first_name,
                        last_name: user?.last_name,
                        username: user?.username,
                        avatar: null,
                      }}
                      validationSchema={validationSchema}
                      onSubmit={async (values, { setSubmitting }) => {
                        const formData = new FormData();
                        if (avatar) {
                          formData.append("avatar", avatar);
                        }
                        formData.append("first_name", values.first_name);
                        formData.append("last_name", values.last_name);
                        formData.append("username", values.username);
                        setSubmitting(true);
                        const headers = new Headers();
                        headers.append(
                          "Authorization",
                          `Bearer ${user?.token}`
                        );

                        try {
                          const response = await fetch(
                            config.ServerBackEndUrl +
                              (config.ServerBackEndUrl.endsWith("/")
                                ? ""
                                : "/") +
                              "auth/update-user-info",
                            {
                              method: "POST",
                              headers,
                              body: formData,
                            }
                          );
                          const data = await response.json();
                          // check if status is not 200
                          console.log({ data, response });
                          if (!response.ok) {
                            alert.error(data.error);
                          } else {
                            setUser(data.data);
                            alert.success("Profile updated successfully");
                          }
                        } catch (error: any) {
                          console.log({ error });
                          // setFormData(form => ({...form, error: "Something went wrong. Please try again later."}))
                          alert.error(
                            "Something went wrong. Please try again later."
                          );
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                    >
                      {({ isSubmitting }) => (
                        <Form>
                          <div className="md:col-span-2">
                            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
                              <div className="col-span-full flex items-center gap-x-8">
                                <img
                                  src={
                                    avatarPreview
                                      ? avatarPreview
                                      : user?.avatar_url || "/avatar.jpg"
                                  }
                                  alt=""
                                  className="h-24 w-24 flex-none rounded-full bg-gray-800 object-cover"
                                />
                                <div>
                                  <label htmlFor="avatar">
                                    <span className="rounded-md bg-black/10 px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-black/20">
                                      Change avatar
                                    </span>
                                  </label>
                                  <Field
                                    type="file"
                                    name="avatar"
                                    className={"hidden"}
                                    id="avatar"
                                    onChange={handleAvatarChange}
                                  />
                                  <ErrorMessage
                                    name="avatar"
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                  />
                                  <p className="mt-2 text-xs leading-5 text-gray-400">
                                    JPG, GIF or PNG. 1MB max.
                                  </p>
                                </div>
                              </div>

                              <div className="sm:col-span-3">
                                <label
                                  htmlFor="first_name"
                                  className="block text-sm font-medium leading-6 text-black"
                                >
                                  First Name
                                </label>
                                <div className="mt-2">
                                  <Field
                                    type="text"
                                    name="first_name"
                                    id="first_name"
                                    autoComplete="given-name"
                                    className="block w-full rounded-md border-0 bg-black/5 pl-2 py-1.5 text-black shadow-sm ring-1 ring-inset ring-black/10 focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:text-sm sm:leading-6"
                                  />
                                  <ErrorMessage
                                    name="first_name"
                                    component="div"
                                    className="text-red-500 text-sm"
                                  />
                                </div>
                              </div>

                              <div className="sm:col-span-3">
                                <label
                                  htmlFor="last_name"
                                  className="block text-sm font-medium leading-6 text-black"
                                >
                                  Last Name
                                </label>
                                <div className="mt-2">
                                  <Field
                                    type="text"
                                    name="last_name"
                                    id="last_name"
                                    autoComplete="given-name"
                                    className="block w-full rounded-md border-0 bg-black/5 pl-2 py-1.5 text-black shadow-sm ring-1 ring-inset ring-black/10 focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:text-sm sm:leading-6"
                                  />
                                  <ErrorMessage
                                    name="last_name"
                                    component="div"
                                    className="text-red-500 text-sm"
                                  />
                                </div>
                              </div>

                              <div className="col-span-full">
                                <label
                                  htmlFor="email"
                                  className="block text-sm font-medium leading-6 text-black"
                                >
                                  Email address
                                </label>
                                <div className="mt-2">
                                  <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={user?.email}
                                    disabled
                                    className="block w-full rounded-md border-0 bg-black/5 pl-2 py-1.5 text-black shadow-sm ring-1 ring-inset ring-black/10 focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:text-sm sm:leading-6"
                                  />
                                </div>
                              </div>

                              <div className="col-span-full">
                                <label
                                  htmlFor="username"
                                  className="block text-sm font-medium leading-6 text-black"
                                >
                                  Username
                                </label>
                                <div className="mt-2">
                                  <Field
                                    type="text"
                                    name="username"
                                    id="username"
                                    autoComplete="username"
                                    className="block w-full rounded-md border-0 bg-black/5 pl-2 py-1.5 text-black shadow-sm ring-1 ring-inset ring-black/10 focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:text-sm sm:leading-6"
                                  />
                                  <ErrorMessage
                                    name="username"
                                    component="div"
                                    className="text-red-500 text-sm"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="mt-8 flex">
                              <button
                                type="submit"
                                disabled={isSubmitting}
                                className="rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                    <div className={"justify-end items-start hidden md:flex"}>
                      <button
                        onClick={() => {
                          navigate("/");
                        }}
                        className={`inline-flex items-center gap-x-1.5 rounded-md bg-primary-600 px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 mr-2
							        ${
                        config.helperMethodsActionSize === "small"
                          ? " text-xs my-1 mr-1 py-[2px] px-1"
                          : config.helperMethodsActionSize === "large"
                          ? " text-base my-2 mr-2 py-2 px-3"
                          : " text-sm my-2 mr-2 py-1 px-3"
                      }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                          />
                        </svg>
                        <span>{config.settingsGoHomepageButtonText}</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
                    <div>
                      <h2 className="text-base font-semibold leading-7 text-black">
                        Change password
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-gray-400">
                        Update your password associated with your account.
                      </p>
                    </div>

                    <Formik
                      initialValues={{
                        current_password: "",
                        new_password: "",
                        confirm_password: "",
                      }}
                      validationSchema={validationSchemaPassword}
                      onSubmit={(values, { setSubmitting }) => {
                        setSubmitting(true);
                        const headers = new Headers();
                        headers.append(
                          "Authorization",
                          `Bearer ${user?.token}`
                        );
                        headers.append("Content-Type", "application/json");
                        try {
                          fetch(
                            config.ServerBackEndUrl +
                              (config.ServerBackEndUrl.endsWith("/")
                                ? ""
                                : "/") +
                              "auth//update-password",
                            {
                              method: "POST",
                              headers,
                              body: JSON.stringify(values),
                            }
                          ).then(async (response) => {
                            const data = await response.json();
                            if (!response.ok) {
                              alert.error(data.error);
                            } else {
                              alert.success("Password changed successfully");
                            }
                          });
                        } catch (error: any) {
                          console.log({ error });
                          alert.error(
                            "Something went wrong. Please try again later."
                          );
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                    >
                      {({ isSubmitting }) => (
                        <Form className="md:col-span-2">
                          <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
                            <div className="col-span-full">
                              <label
                                htmlFor="current_password"
                                className="block text-sm font-medium leading-6 text-black"
                              >
                                Current password
                              </label>
                              <div className="mt-2">
                                <div className={"relative"}>
                                  <Field
                                    id="current_password"
                                    name="current_password"
                                    type={showPasswordOld ? "text" : "password"}
                                    autoComplete="current_password"
                                    className="block w-full rounded-md border-0 bg-black/5 pl-2 py-1.5 text-black shadow-sm ring-1 ring-inset ring-black/10 focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:text-sm sm:leading-6"
                                  />
                                  <EyeIconPassword
                                    show={showPasswordOld}
                                    onClick={() =>
                                      setShowPasswordOld(!showPasswordOld)
                                    }
                                  />
                                </div>

                                <ErrorMessage
                                  name={"current_password"}
                                  component={"div"}
                                  className={"text-red-500 text-sm"}
                                />
                              </div>
                            </div>

                            <div className="col-span-full">
                              <label
                                htmlFor="new_password"
                                className="block text-sm font-medium leading-6 text-black"
                              >
                                New password
                              </label>
                              <div className="mt-2">
                                <div className={"relative"}>
                                  <Field
                                    id="new_password"
                                    name="new_password"
                                    type={showPasswordNew ? "text" : "password"}
                                    autoComplete="new_password"
                                    className="block w-full rounded-md border-0 bg-black/5 pl-2 py-1.5 text-black shadow-sm ring-1 ring-inset ring-black/10 focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:text-sm sm:leading-6"
                                  />
                                  <EyeIconPassword
                                    show={showPasswordNew}
                                    onClick={() =>
                                      setShowPasswordNew(!showPasswordNew)
                                    }
                                  />
                                </div>
                                <ErrorMessage
                                  name={"new_password"}
                                  component={"div"}
                                  className={"text-red-500 text-sm"}
                                />
                              </div>
                            </div>

                            <div className="col-span-full">
                              <label
                                htmlFor="confirm_password"
                                className="block text-sm font-medium leading-6 text-black"
                              >
                                Confirm password
                              </label>
                              <div className="mt-2">
                                <div className={"relative"}>
                                  <Field
                                    id="confirm_password"
                                    name="confirm_password"
                                    type={
                                      showPasswordConfirm ? "text" : "password"
                                    }
                                    autoComplete="new_password"
                                    className="block w-full rounded-md border-0 bg-black/5 pl-2 py-1.5 text-black shadow-sm ring-1 ring-inset ring-black/10 focus:ring-1 focus:ring-inset focus:ring-primary-500 sm:text-sm sm:leading-6"
                                  />
                                  <EyeIconPassword
                                    show={showPasswordConfirm}
                                    onClick={() =>
                                      setShowPasswordConfirm(
                                        !showPasswordConfirm
                                      )
                                    }
                                  />
                                </div>
                                <ErrorMessage
                                  name={"confirm_password"}
                                  component={"div"}
                                  className={"text-red-500 text-sm"}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="mt-8 flex items-center justify-between">
                            <VersionDisplay showLabel={true} />
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                            >
                              Save
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </div>
              </main>
            )}
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
};
